import { action, makeObservable, observable, runInAction, observe } from 'mobx';
import { addPrinterReq, editPrinterReq, getPrinters, removePrinterReq } from '../api/admin';
import CompanyStore from './CompanyStore';
import Printer from '../model/Printer';

class PrinterStore {
  companyStore: CompanyStore;

  list: Printer[] = [];

  loading = false;

  errorMessage: null | string = null;

  constructor(companyStore: CompanyStore) {
    this.companyStore = companyStore;

    makeObservable(this, {
      list: observable,
      loading: observable,
      errorMessage: observable,
      fetchPrinters: action,
      addPrinter: action,
      refreshPrinters: action,
      removePrinter: action,
      updatePrinter: action,
    });
    observe(this.companyStore, 'companyKey', async ({ newValue }) => {
      if (newValue) {
        this.initialize();
      } else {
        this.clear();
      }
    });
  }

  async initialize() {
    if (!this.companyStore.companyKey) {
      return;
    }
    runInAction(() => {
      this.list = [];
      this.fetchPrinters();
    });
  }

  clear() {
    this.list = [];
  }

  fetchPrinters() {
    if (!this.companyStore.companyKey || this.loading || this.list.length !== 0) {
      return;
    }
    this.loading = true;
    getPrinters(this.companyStore.companyKey)
      .then((res) => {
        runInAction(() => {
          this.list = res;
        });
      })
      .catch(() => {
        runInAction(() => {
          this.errorMessage = 'Ett fel har inträffat';
        });
      })
      .finally(() => {
        runInAction(() => {
          this.loading = false;
        });
      });
  }

  refreshPrinters() {
    if (this.loading) {
      return;
    }
    this.list = [];
    this.fetchPrinters();
  }

  async addPrinter(printer: Omit<Printer, 'id'>) {
    const { companyKey } = this.companyStore;
    if (!companyKey) {
      runInAction(() => {
        this.errorMessage = 'Inget företag valt';
      });
      return;
    }

    await addPrinterReq(companyKey, printer)
      .then((res: Printer) => {
        runInAction(() => {
          this.list.push(res);
        });
      })
      .catch((err) => {
        this.errorMessage = err.message;
      });
  }

  removePrinter = async (id: string) => {
    if (!this.companyStore.companyKey || !id) {
      return;
    }

    await removePrinterReq(this.companyStore.companyKey, id)
      .then(() => {
        runInAction(() => {
          this.list = this.list.filter((printer) => printer.id !== id);
        });
      })
      .catch((err) => {
        this.errorMessage = err.message;
      });
  };

  updatePrinter = async (printer: Printer) => {
    if (!this.companyStore.companyKey) {
      return;
    }

    await editPrinterReq(this.companyStore.companyKey, printer)
      .then(() => {
        runInAction(() => {
          this.list = this.list.map((item) => (item.id === printer.id ? printer : item));
        });
      })
      .catch((err) => {
        runInAction(() => {
          this.errorMessage = err.message;
        });
      });
  };
}

export default PrinterStore;
